import { useCallback, useEffect, useRef, useState } from 'react'
import { Gift, Loader2, Pencil, RotateCcw, Search, XCircle } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { apiFetch } from '../api/client'
import AdminToolNav from '../components/admin/AdminToolNav'
import { createNadDebug } from '../utils/debugLog'

const log = createNadDebug('dw:free-trial-billing-admin:ui')

const TrialTargetSchema = z.object({
  team_id: z.string(),
  team_name: z.string(),
  team_kind: z.string(),
  owner_email: z.string().nullable(),
})
const TrialRecordSchema = TrialTargetSchema.extend({
  starts_at: z.string(),
  expires_at: z.string(),
  credit_limit: z.number().int(),
  committed_credits: z.number().int(),
  revoked_at: z.string().nullable(),
  reason: z.string().nullable(),
})
const TrialListSchema = z.object({ entitlements: z.array(TrialRecordSchema) })
type TrialTarget = z.infer<typeof TrialTargetSchema>
type TrialRecord = z.infer<typeof TrialRecordSchema>
type FormMode = 'add' | 'edit' | 'reactivate'
type TrialListState = 'loading' | 'ready' | 'error'

async function jsonRequest<T extends z.ZodType>(
  path: string,
  schema: T,
  init?: RequestInit,
): Promise<z.infer<T>> {
  const response = await apiFetch(path, init)
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    const detail = payload?.detail
    throw new Error(typeof detail === 'string' ? detail : detail?.message ?? `Request failed: ${response.status}`)
  }
  return schema.parse(payload)
}

function localInputValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

function targetFromRecord(record: TrialRecord): TrialTarget {
  return TrialTargetSchema.parse(record)
}

function trialStatus(record: TrialRecord): 'scheduled' | 'active' | 'expired' | 'ended' {
  if (record.revoked_at !== null) return 'ended'
  const now = Date.now()
  if (new Date(record.starts_at).getTime() > now) return 'scheduled'
  if (new Date(record.expires_at).getTime() <= now) return 'expired'
  return 'active'
}

export default function AdminFreeTrialsPage() {
  const { t } = useTranslation('pages')
  const [params] = useSearchParams()
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [teamId, setTeamId] = useState(params.get('team_id') ?? '')
  const [target, setTarget] = useState<TrialTarget | null>(null)
  const [mode, setMode] = useState<FormMode>('add')
  const [startsAt, setStartsAt] = useState(() => localInputValue(new Date()))
  const [expiresAt, setExpiresAt] = useState(() => localInputValue(new Date(Date.now() + 7 * 86_400_000)))
  const [creditLimit, setCreditLimit] = useState('1000')
  const [reason, setReason] = useState('')
  const [records, setRecords] = useState<TrialRecord[]>([])
  const [listState, setListState] = useState<TrialListState>('loading')
  const [listError, setListError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [endingTeamId, setEndingTeamId] = useState<string | null>(null)
  const listRequestId = useRef(0)

  const load = useCallback(async () => {
    const requestId = ++listRequestId.current
    try {
      const data = await jsonRequest('/admin/free-trials', TrialListSchema)
      if (requestId !== listRequestId.current) return
      setRecords(data.entitlements)
      setListState('ready')
      setListError(null)
      log({ event: 'free-trial-admin:list-state', state: 'ready', recordCount: data.entitlements.length })
    } catch (err) {
      if (requestId !== listRequestId.current) return
      const message = err instanceof Error ? err.message : String(err)
      setListError(message)
      setListState('error')
      log({ event: 'free-trial-admin:list-state', state: 'error' })
      throw err
    }
  }, [])

  useEffect(() => {
    log({ event: 'free-trial-admin:list-state', state: 'loading' })
    void load().catch(() => undefined)
  }, [load])

  const closeForm = () => {
    setTarget(null)
    setMode('add')
    setError(null)
  }

  const openRecord = (record: TrialRecord, nextMode: 'edit' | 'reactivate') => {
    setTarget(targetFromRecord(record))
    setMode(nextMode)
    setStartsAt(localInputValue(new Date(record.starts_at)))
    setExpiresAt(localInputValue(new Date(record.expires_at)))
    setCreditLimit(String(record.credit_limit))
    setReason(record.reason ?? '')
    setError(null)
  }

  const resolve = async () => {
    setBusy(true)
    setError(null)
    try {
      if (email.trim() && teamId.trim()) {
        throw new Error(t('adminFreeTrials.chooseOneTarget'))
      }
      const resolvingByEmail = email.trim().length > 0
      const query = email.trim()
        ? `email=${encodeURIComponent(email.trim())}`
        : `team_id=${encodeURIComponent(teamId.trim())}`
      const next = await jsonRequest(`/admin/free-trials/resolve?${query}`, TrialTargetSchema)
      const existing = records.find((record) => record.team_id === next.team_id)
      if (existing) {
        openRecord(existing, existing.revoked_at === null ? 'edit' : 'reactivate')
      } else {
        setTarget(next)
        setMode('add')
        setStartsAt(localInputValue(new Date()))
        setExpiresAt(localInputValue(new Date(Date.now() + 7 * 86_400_000)))
        setCreditLimit('1000')
        setReason('')
      }
      if (!resolvingByEmail) setTeamId(next.team_id)
      log({ event: 'free-trial-admin:target_resolved', team_kind: next.team_kind })
    } catch (err) {
      setTarget(null)
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (!target) return
    setBusy(true)
    setError(null)
    try {
      const numericLimit = Number(creditLimit)
      const existing = records.find((record) => record.team_id === target.team_id)
      if (existing && numericLimit < existing.committed_credits) {
        throw new Error(t('adminFreeTrials.limitBelowCommitted', {
          used: existing.committed_credits.toLocaleString(),
        }))
      }
      if (mode === 'reactivate' && new Date(expiresAt).getTime() <= Date.now()) {
        throw new Error(t('adminFreeTrials.expiryMustBeFuture'))
      }
      const method = mode === 'edit' ? 'PUT' : 'POST'
      const path = mode === 'edit'
        ? `/admin/free-trials/${encodeURIComponent(target.team_id)}`
        : '/admin/free-trials'
      await jsonRequest(path, TrialRecordSchema, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          team_id: target.team_id,
          starts_at: new Date(startsAt).toISOString(),
          expires_at: new Date(expiresAt).toISOString(),
          credit_limit: numericLimit,
          reason: reason.trim() || null,
        }),
      })
      log({
        event: mode === 'edit'
          ? 'free-trial-admin:updated'
          : mode === 'reactivate'
            ? 'free-trial-admin:reactivated'
            : 'free-trial-admin:saved',
        team_kind: target.team_kind,
      })
      await load()
      closeForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const endTrial = async (record: TrialRecord) => {
    setBusy(true)
    setError(null)
    try {
      await jsonRequest(
        `/admin/free-trials/${encodeURIComponent(record.team_id)}`,
        TrialRecordSchema,
        { method: 'DELETE' },
      )
      log({ event: 'free-trial-admin:ended', team_kind: record.team_kind })
      await load()
      setEndingTeamId(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8 text-foreground">
      <AdminToolNav />
      <div className="mb-6">
        <h1 className="text-xl font-semibold">{t('adminFreeTrials.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('adminFreeTrials.description')}</p>
      </div>

      <section className="border-y border-border-subtle py-5">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <input
            value={email}
            onChange={(event) => { setEmail(event.target.value); setTeamId(''); setTarget(null) }}
            placeholder={t('adminFreeTrials.emailPlaceholder')}
            className="h-9 rounded-lg border border-input-border bg-input-bg px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <input
            value={teamId}
            onChange={(event) => { setTeamId(event.target.value); setEmail(''); setTarget(null) }}
            placeholder={t('adminFreeTrials.teamPlaceholder')}
            className="h-9 rounded-lg border border-input-border bg-input-bg px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            type="button"
            onClick={() => void resolve()}
            disabled={busy || (!email.trim() && !teamId.trim())}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {t('adminFreeTrials.find')}
          </button>
        </div>

        {target && (
          <div className="mt-5 grid gap-3 border-t border-border-subtle pt-5 md:grid-cols-2">
            <div className="md:col-span-2 text-sm">
              <span className="font-medium">{target.team_name}</span>
              <span className="ml-2 text-muted-foreground">{target.owner_email ?? target.team_id}</span>
            </div>
            <label className="text-sm text-muted-foreground">
              {t('adminFreeTrials.startsAt')}
              <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="mt-1 h-9 w-full rounded-lg border border-input-border bg-input-bg px-3 text-foreground" />
            </label>
            <label className="text-sm text-muted-foreground">
              {t('adminFreeTrials.expiresAt')}
              <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="mt-1 h-9 w-full rounded-lg border border-input-border bg-input-bg px-3 text-foreground" />
            </label>
            <label className="text-sm text-muted-foreground">
              {t('adminFreeTrials.creditLimit')}
              <input
                type="number"
                min={Math.max(
                  1,
                  records.find((record) => record.team_id === target.team_id)?.committed_credits ?? 1,
                )}
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                className="mt-1 h-9 w-full rounded-lg border border-input-border bg-input-bg px-3 text-foreground"
              />
            </label>
            <label className="text-sm text-muted-foreground">
              {t('adminFreeTrials.reason')}
              <input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-9 w-full rounded-lg border border-input-border bg-input-bg px-3 text-foreground" />
            </label>
            <div className="flex justify-end gap-2 md:col-span-2">
              <button
                type="button"
                onClick={closeForm}
                disabled={busy}
                className="h-9 rounded-lg border border-input-border bg-surface-1 px-4 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 disabled:opacity-40"
              >
                {t('adminFreeTrials.cancel')}
              </button>
              <button type="button" onClick={() => void save()} disabled={busy || Number(creditLimit) < 1} className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-40">
                {mode === 'reactivate' ? <RotateCcw className="h-4 w-4" /> : <Gift className="h-4 w-4" />}
                {t(mode === 'edit'
                  ? 'adminFreeTrials.saveChanges'
                  : mode === 'reactivate'
                    ? 'adminFreeTrials.reactivate'
                    : 'adminFreeTrials.save')}
              </button>
            </div>
          </div>
        )}
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      </section>

      <section className="mt-7">
        <h2 className="text-lg font-medium">{t('adminFreeTrials.current')}</h2>
        <div className="mt-3 divide-y divide-border-subtle border-y border-border-subtle">
          {listState === 'loading' && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground" role="status">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('adminFreeTrials.loading')}
            </div>
          )}
          {listState === 'error' && listError && (
            <p data-testid="admin-free-trials-list-error" className="py-6 text-sm text-destructive">
              {listError}
            </p>
          )}
          {records.map((record) => (
            <div key={record.team_id} className="grid gap-2 py-3 text-sm md:grid-cols-[1fr_auto_auto] md:items-center">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{record.team_name}</span>
                  <span className="rounded-full border border-border-subtle bg-surface-1 px-2 py-0.5 text-xs text-muted-foreground">
                    {t(`adminFreeTrials.status.${trialStatus(record)}`)}
                  </span>
                </div>
                <div className="text-muted-foreground">
                  {t('adminFreeTrials.usage', {
                    used: record.committed_credits.toLocaleString(),
                    limit: record.credit_limit.toLocaleString(),
                  })}
                </div>
                {record.reason && <div className="text-muted-foreground">{record.reason}</div>}
              </div>
              <div className="text-muted-foreground">
                <div>{new Date(record.expires_at).toLocaleDateString()}</div>
                {record.revoked_at && (
                  <div>{t('adminFreeTrials.endedAt', {
                    date: new Date(record.revoked_at).toLocaleDateString(),
                  })}</div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => openRecord(record, 'edit')}
                  disabled={busy}
                  title={t('adminFreeTrials.edit')}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground hover:border-input-border-hover hover:bg-surface-3 disabled:opacity-30"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                {record.revoked_at === null ? (
                  endingTeamId === record.team_id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setEndingTeamId(null)}
                        disabled={busy}
                        className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3"
                      >
                        {t('adminFreeTrials.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void endTrial(record)}
                        disabled={busy}
                        className="h-9 rounded-lg bg-destructive px-3 text-xs font-medium text-white hover:bg-destructive/90 disabled:opacity-40"
                      >
                        {t('adminFreeTrials.endConfirm')}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEndingTeamId(record.team_id)}
                      disabled={busy}
                      title={t('adminFreeTrials.endTrial')}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive disabled:opacity-30"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  )
                ) : (
                  <button
                    type="button"
                    onClick={() => openRecord(record, 'reactivate')}
                    disabled={busy}
                    title={t('adminFreeTrials.reactivate')}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground hover:border-input-border-hover hover:bg-surface-3 disabled:opacity-30"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {listState === 'ready' && records.length === 0 && <p className="py-6 text-sm text-muted-foreground">{t('adminFreeTrials.empty')}</p>}
        </div>
      </section>
    </main>
  )
}
